/**
 * The draft poll's shared, pure pieces (WP-F6) — the query key + cadence both draft screens use,
 * and the progressive-reveal derivation. Pure and React-free so the reveal logic is unit-tested
 * directly, and so Review does not have to import from Processing to share a query key.
 */
import type { DraftState } from './api-types';

/**
 * How often to ask while the pipeline runs. It takes ~30–60s (transcribe → quality gate → extract
 * → moderate → draft), so this is often enough to feel live without hammering the endpoint.
 */
export const DRAFT_POLL_INTERVAL_MS = 2_500;

export function refereeDraftQueryKey(token: string) {
  return ['referee-draft', token] as const;
}

/** Which of the three processing beats is currently underway. */
export type Beat = 0 | 1 | 2;

/**
 * Which beat the pipeline is on, derived ONLY from what the poll has actually returned:
 *   0 — nothing back yet            → "Listening…" is live
 *   1 — the transcript has landed   → "Pulling out the key points…" is live
 *   2 — the key points have landed  → "Writing the letter…" is live
 *
 * This is what makes the reveal earned rather than timed: each beat is evidence that a real stage
 * finished, so it cannot desynchronise from the pipeline the way a timer would the moment the
 * pipeline ran long or short.
 *
 * Note a `no_key_points` divert never reaches beat 2 — it leaves for the under-review page from
 * beat 1 — so an empty `keyPoints` array is a normal shape here, not a missing one.
 */
export function beatFor(draft: DraftState | undefined): Beat {
  if (!draft) return 0;
  if (draft.keyPoints.length > 0) return 2;
  if (draft.transcript) return 1;
  return 0;
}
